import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, Heart, ChevronRight, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { cn } from "@/lib/utils";
import { useFavorites } from "@/hooks/use-favorites";

interface HeaderProps {
  variant?: "default" | "movers";
}

const navCategories = [
  {
    label: "Verhuisbedrijf",
    links: [
      { href: "/verhuisbedrijven", label: "Verhuisbedrijven vergelijken" },
      { href: "/verhuisoffertes", label: "Gratis offertes aanvragen" },
      { href: "/verhuiskosten", label: "Verhuiskosten 2026" },
      { href: "/goedkoop-verhuizen", label: "Goedkoop verhuizen" },
    ],
  },
  {
    label: "Specialistisch",
    links: [
      { href: "/piano-verhuizen", label: "Piano verhuizen" },
      { href: "/kluis-verhuizen", label: "Kluis verhuizen" },
      { href: "/kunst-verhuizen", label: "Kunst verhuizen" },
      { href: "/aquarium-verhuizen", label: "Aquarium verhuizen" },
      { href: "/senioren-verhuizen", label: "Senioren verhuizen" },
      { href: "/studentenverhuizing", label: "Studentenverhuizing" },
    ],
  },
  {
    label: "In jouw regio",
    links: [
      { href: "/verhuisoffertes/amsterdam", label: "Amsterdam" },
      { href: "/verhuisoffertes/rotterdam", label: "Rotterdam" },
      { href: "/verhuisoffertes/den-haag", label: "Den Haag" },
      { href: "/verhuisoffertes/utrecht", label: "Utrecht" },
      { href: "/verhuisoffertes/eindhoven", label: "Eindhoven" },
      { href: "/verhuisoffertes/groningen", label: "Groningen" },
    ],
  },
  {
    label: "Emigreren",
    links: [
      { href: "/verhuizen-naar-belgie", label: "Verhuizen naar België" },
      { href: "/verhuizen-naar-duitsland", label: "Verhuizen naar Duitsland" },
    ],
  },
  {
    label: "Tools & tips",
    links: [
      { href: "/verhuistips", label: "Verhuistips" },
      { href: "/verhuischecklist", label: "Verhuischecklist" },
      { href: "/volume-calculator", label: "Volume calculator" },
      { href: "/adreswijziging-doorgeven", label: "Adreswijziging doorgeven" },
      { href: "/energiecontract-verhuizen", label: "Energiecontract regelen" },
      { href: "/verzekeringen-aanpassen", label: "Verzekeringen aanpassen" },
    ],
  },
];

const Header = ({ variant = "default" }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { favorites } = useFavorites();

  const isMoversPage = variant === "movers" || location.pathname.includes("voor-verhuizers");

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar */}
      <div className="bg-background border-b border-border/50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Logo />

          <div className="hidden md:flex items-center gap-3">
            <Link to="/favorieten" className="relative" aria-label="Favorieten">
              <Button variant="ghost" size="icon" className="relative">
                <Heart className={cn("w-5 h-5", favorites.length > 0 && "fill-destructive text-destructive")} />
                {favorites.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs font-bold rounded-full flex items-center justify-center">
                    {favorites.length > 9 ? "9+" : favorites.length}
                  </span>
                )}
              </Button>
            </Link>
            {isMoversPage ? (
              <Link to="/">
                <Button variant="ghost" className="font-medium text-sm">
                  Voor Klanten
                </Button>
              </Link>
            ) : (
              <Link to="/voor-verhuizers" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Meld je aan als verhuizer
              </Link>
            )}
            <Link to="/auth">
              <Button variant="outline" size="sm" className="font-medium">
                Inloggen
              </Button>
            </Link>
            <Link to="/quote">
              <Button size="sm" className="font-semibold shadow-md">
                Ontvang offertes
              </Button>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Menu openen</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[340px] overflow-y-auto">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-6">
                    <Logo size="sm" />
                  </div>

                  <div className="flex flex-col gap-1">
                    {navCategories.map((category) => (
                      <div key={category.label} className="mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 py-1.5">
                          {category.label}
                        </p>
                        {category.links.map((link) => (
                          <Link
                            key={link.href}
                            to={link.href}
                            onClick={() => setIsOpen(false)}
                          >
                            <Button
                              variant="ghost"
                              className="w-full justify-start font-medium h-9 pl-3 text-sm"
                            >
                              {link.label}
                            </Button>
                          </Link>
                        ))}
                      </div>
                    ))}

                    <div className="border-t border-border my-3" />

                    <Link to="/blog" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start font-medium text-base h-11">
                        Blog
                      </Button>
                    </Link>
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start font-medium text-base h-11">
                        Contact
                      </Button>
                    </Link>

                    <div className="border-t border-border my-3" />

                    <Link to="/favorieten" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start font-medium text-base h-11">
                        <Heart className={cn("w-5 h-5 mr-2", favorites.length > 0 && "fill-destructive text-destructive")} />
                        Favorieten {favorites.length > 0 && `(${favorites.length})`}
                      </Button>
                    </Link>

                    <Link to="/auth" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full font-medium h-11">
                        Inloggen
                      </Button>
                    </Link>

                    <Link to="/quote" onClick={() => setIsOpen(false)}>
                      <Button className="w-full font-semibold h-11 mt-2">
                        Vraag gratis offertes aan
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Bottom nav bar - desktop only */}
      <div className="hidden md:block bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <NavigationMenu className="max-w-none w-full justify-start" useViewport={false}>
            <NavigationMenuList className="gap-0">
              {navCategories.map((category) => (
                <NavigationMenuItem key={category.label} className="relative">
                  <NavigationMenuTrigger
                    className="bg-transparent text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 data-[state=open]:bg-primary-foreground/15 rounded-none h-11 px-4 text-sm font-medium"
                  >
                    {category.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="absolute left-0 top-full mt-1.5 min-w-[220px] rounded-md border bg-popover text-popover-foreground shadow-lg">
                    <div className="p-3">
                      {category.links.map((link) => (
                        <NavigationMenuLink key={link.href} asChild>
                          <Link
                            to={link.href}
                            className="flex items-center justify-between gap-2 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors group"
                          >
                            <span>{link.label}</span>
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        </NavigationMenuLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ))}

              {/* Direct links */}
              <NavigationMenuItem>
                <Link
                  to="/blog"
                  className="inline-flex h-11 items-center px-4 text-sm font-medium text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                >
                  Blog
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to="/contact"
                  className="inline-flex h-11 items-center px-4 text-sm font-medium text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                >
                  Contact
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>
    </nav>
  );
};

export default Header;
